import type { AppLocale } from '@/i18n';

/**
 * Formats a date as dual Umm al-Qura Hijri + Gregorian — the app-wide rule.
 * e.g. ar: "١٥ محرم ١٤٤٨ هـ — ١٠ يوليو ٢٠٢٦"; en: "Muharram 15, 1448 AH — July 10, 2026".
 */
export function formatDualDate(date: Date, locale: AppLocale): string {
  const hijri = new Intl.DateTimeFormat(`${locale}-u-ca-islamic-umalqura`, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
  const gregorian = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
  return `${hijri} — ${gregorian}`;
}

/** Formats a Date as a Postgres `date` literal (YYYY-MM-DD), ignoring time. */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear().toString().padStart(4, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parses a `YYYY-MM-DD` date-only string into a local Date at midnight. */
export function parseIsoDate(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Today's date at local midnight. */
export function today(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Today as a Postgres `date` literal. */
export function todayIso(): string {
  return toIsoDate(today());
}

/** Friday = weekday 5 (0=Sunday…6=Saturday), matching both `Date.getDay()` and
 * Postgres `EXTRACT(DOW FROM date)` — no conversion needed between them. */
export function isFriday(date: Date): boolean {
  return date.getDay() === 5;
}

/** Whether the inclusive [startIso, endIso] date range contains a Friday.
 * Jumu'ah can only be requested/offered for a range that includes one. */
export function dateRangeIncludesFriday(startIso: string, endIso: string): boolean {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    if (isFriday(d)) return true;
  }
  return false;
}

/** All ISO dates in the inclusive [startIso, endIso] range. */
export function isoDateRange(startIso: string, endIso: string): string[] {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  const out: string[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(toIsoDate(d));
  }
  return out;
}
